import type { PlanNode } from './project-document.js';

/**
 * BR-16's soft validations on a plan node's three dates.
 *
 * BR-16 calls these **warnings, not rejects**: the PM's date is kept and the
 * disagreement is reported. That is the same treatment a typed hours figure
 * gets (BR-12's retirement, ADR 0034) — the number a person typed is theirs.
 *
 * Until dates could only be dragged, ordering was enforced geometrically: an
 * edge cannot be dragged past its own twin, and a parent floors at its
 * descendants (ADR 0020). A typed field has no such geometry, which is why
 * BR-16 stops being optional the moment one exists.
 *
 * Comparison is lexicographic, which is chronological for this format — both
 * operands are `YYYY-MM-DD` enforced by the schema's regex. That regex admits
 * an impossible day (2026-02-31), so a warning here is about ORDER only; the
 * calendar field is what stops an impossible date being entered in the first
 * place.
 */

export type PlanDateWarningCode = 'start-after-end' | 'end-after-target';

export type PlanDateWarning = {
  code: PlanDateWarningCode;
  /** The field to attach the message to, when a surface places them per-field. */
  field: 'start' | 'end' | 'target';
  message: string;
};

/**
 * The BR-16 violations a node can produce on its own.
 *
 * BR-16's third validation — a child's range falling outside its parent's —
 * needs the tree rather than one node, and is deliberately not covered here.
 * It remains specified and unimplemented.
 */
export const planDateWarnings = (node: PlanNode): PlanDateWarning[] => {
  const warnings: PlanDateWarning[] = [];

  if (node.startDate !== undefined && node.endDate !== undefined && node.startDate > node.endDate) {
    warnings.push({
      code: 'start-after-end',
      field: 'start',
      message: 'Starts after it ends.',
    });
  }

  /*
    The target is the deadline (domain model: "the deadline signal"), so the
    disagreement is the work ending AFTER it, the timeline mark's "runs past
    it". A target after the end is slack, which the mark reads as "days to
    spare". The comparison ran the other way until 2026-10-01, when the pilot
    noticed the two surfaces contradicting each other.
  */
  if (
    node.targetDate !== undefined &&
    node.endDate !== undefined &&
    node.endDate > node.targetDate
  ) {
    warnings.push({
      code: 'end-after-target',
      field: 'end',
      message: 'The work ends after its target date.',
    });
  }

  return warnings;
};
